'use client';
import React from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const principles = [
  {
    tag: '[01]',
    title: 'Embedded, not external',
    desc: 'We work inside your team, in your sprints, your standups, your Slack. Not as an external vendor who delivers a report and disappears. The security knowledge stays with your organisation.',
  },
  {
    tag: '[02]',
    title: 'Continuous, not point-in-time',
    desc: 'Annual penetration tests and point-in-time compliance assessments create a false sense of security. Real security is continuous, monitoring, testing, and improving every day of the year.',
  },
  {
    tag: '[03]',
    title: 'Risk-led, not checkbox-driven',
    desc: 'Compliance frameworks are a floor, not a ceiling. We prioritise by real business risk, what would actually hurt your organisation, not by what is easiest to evidence for an auditor.',
  },
  {
    tag: '[04]',
    title: 'Senior engineers only',
    desc: 'Every TechSonar engagement is staffed with senior engineers. No juniors learning on your time. No automated-only outputs dressed up as expert analysis. Real expertise, applied directly.',
  },
];

const howWeWork = [
  {
    phase: 'Understand',
    desc: 'We start by understanding your technology, your team, your risk profile, and your regulatory obligations. Not a generic questionnaire, a real conversation with your engineers and leadership.',
  },
  {
    phase: 'Design',
    desc: 'We design an engagement that fits your situation. Whether that is a rapid accelerator programme, a continuous DevSecOps engagement, or a bespoke security programme, it is scoped to your actual needs.',
  },
  {
    phase: 'Embed',
    desc: 'Our engineers work inside your team. We use your tools, your processes, your communication channels. Security becomes part of how you work, not something that happens to you.',
  },
  {
    phase: 'Sustain',
    desc: 'We build capability inside your organisation. When we leave, the security posture, the processes, and the knowledge remain. We measure success by how little you need us over time.',
  },
];

const expertise = [
  'Application Security (SAST, DAST, SCA)',
  'DevSecOps & CI/CD Pipeline Security',
  'SOC 2 Type II & ISO 27001',
  'DORA & FCA Compliance',
  'Threat Modelling & Security Architecture',
  'IaC & Cloud Security (AWS, Azure, GCP)',
  'GDPR & Data Protection Engineering',
  'NIS2 & Critical Infrastructure',
  'Kubernetes & Container Security',
  'Supply Chain & SBOM Security',
  'Incident Response & Forensics',
  'Security Programme Design',
];

export default function AboutPage() {
  useScrollAnimation();

  return (
    <div className="min-h-screen bg-[#050a0f] text-[#f5f5f8] noise-overlay-animated">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="orb orb-blue" style={{ width: '700px', height: '700px', top: '-200px', left: '-150px', opacity: 0.25 }} />
          <div className="orb orb-teal" style={{ width: '500px', height: '500px', bottom: '-100px', right: '0', opacity: 0.15 }} />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-3 mb-6 fade-in-up">
            <Link href="/" className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest hover:text-[#80edfa] transition-colors">Home</Link>
            <span className="text-[#333]">/</span>
            <span className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest">About</span>
          </div>
          <div className="max-w-[900px]">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4 fade-in-up" style={{ transitionDelay: '0.05s' }}>
              [About TechSonar]
            </p>
            <h1 className="font-heading text-[clamp(2.5rem,6vw,4.5rem)] text-[#f5f5f8] font-medium leading-[1.05] mb-6 fade-in-up" style={{ transitionDelay: '0.1s' }}>
              Security engineered in.<br />
              <span className="text-[#80edfa]">Not bolted on.</span>
            </h1>
            <p className="text-[#aaa] text-lg leading-relaxed max-w-[680px] fade-in-up" style={{ transitionDelay: '0.15s' }}>
              TechSonar is a specialist DevSecOps consultancy. We embed senior security engineers directly into engineering teams to build security into the way organisations design, build, and operate software, continuously, not as a one-off exercise.
            </p>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">
            <div className="fade-in-up">
              <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest mb-4">Our mission</p>
              <h2 className="font-heading text-[clamp(1.8rem,4vw,3rem)] text-[#f5f5f8] font-medium leading-tight mb-6">
                Make security a competitive advantage, not a compliance burden.
              </h2>
              <p className="text-[#aaa] leading-relaxed mb-6">
                Most organisations treat security as something that happens to them, audits, incidents, regulatory notices. We believe security should be engineered into the way you build and operate software from the start.
              </p>
              <p className="text-[#aaa] leading-relaxed mb-6">
                That means senior engineers embedded in your team, working in your delivery process, building security capability that stays with your organisation long after the engagement ends.
              </p>
              <p className="text-[#aaa] leading-relaxed">
                It means compliance programmes that do not end at certification, continuous evidence collection, control monitoring, and embedded support through every audit window. And it means prioritisation driven by real business risk, not by what is easiest to evidence for an auditor.
              </p>
            </div>
            <div className="fade-in-up" style={{ transitionDelay: '0.1s' }}>
              <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest mb-6">Areas of expertise</p>
              <div className="grid grid-cols-1 gap-px bg-[#222]">
                {expertise?.map((item, i) => (
                  <div key={i} className="bg-[#050a0f] px-6 py-4 flex items-center gap-4">
                    <div className="w-1 h-1 rounded-full bg-[#80edfa] flex-shrink-0" />
                    <span className="text-sm text-[#aaa] font-sans">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section id="how-we-work" className="border-t border-[#222] py-20 lg:py-28 bg-[#0a0f14]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-12 fade-in-up">
            <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest mb-2">Our principles</p>
            <h2 className="font-heading text-[clamp(1.8rem,4vw,3rem)] text-[#f5f5f8] font-medium leading-tight max-w-[600px]">
              How we think about security.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#222]">
            {principles?.map((p, i) => (
              <div key={i} className="bg-[#0a0f14] p-8 lg:p-10 fade-in-up" style={{ transitionDelay: `${i * 0.07}s` }}>
                <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-4">{p?.tag}</p>
                <h3 className="font-heading text-xl text-[#f5f5f8] font-medium mb-3">{p?.title}</h3>
                <p className="text-sm text-[#666] leading-relaxed">{p?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How we work */}
      <section className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-12 fade-in-up">
            <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest mb-2">Engagement model</p>
            <h2 className="font-heading text-[clamp(1.8rem,4vw,3rem)] text-[#f5f5f8] font-medium leading-tight max-w-[600px]">
              How a TechSonar engagement works.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#222]">
            {howWeWork?.map((step, i) => (
              <div key={i} className="bg-[#050a0f] p-8 fade-in-up" style={{ transitionDelay: `${i * 0.07}s` }}>
                <div className="w-8 h-px bg-[#80edfa] mb-6" />
                <p className="font-heading text-lg text-[#f5f5f8] font-medium mb-3">{step?.phase}</p>
                <p className="text-sm text-[#555] leading-relaxed">{step?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Careers */}
      <section id="careers" className="border-t border-[#222] py-20 lg:py-28 bg-[#0a0f14]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="fade-in-up">
              <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest mb-4">Careers</p>
              <h2 className="font-heading text-[clamp(1.8rem,4vw,3rem)] text-[#f5f5f8] font-medium leading-tight mb-6">
                We hire senior engineers. Full stop.
              </h2>
              <p className="text-[#aaa] leading-relaxed mb-6">
                TechSonar does not hire juniors to staff engagements. Every engineer we place is someone we would trust to work on our own systems. If you are a senior security engineer who is tired of writing reports that nobody reads, we should talk.
              </p>
              <p className="text-[#aaa] leading-relaxed mb-8">
                We work with engineers who have deep expertise in AppSec, DevSecOps, cloud security, compliance engineering, or threat modelling, and who want to do real work that makes a real difference.
              </p>
              <Link href="/contact" className="btn-primary">Get in touch</Link>
            </div>
            <div className="fade-in-up" style={{ transitionDelay: '0.1s' }}>
              <div className="grid grid-cols-1 gap-px bg-[#222]">
                {[
                  { role: 'Senior AppSec Engineer', type: 'Permanent / Contract' },
                  { role: 'DevSecOps Engineer', type: 'Permanent / Contract' },
                  { role: 'Cloud Security Architect', type: 'Permanent / Contract' },
                  { role: 'Compliance Engineering Lead', type: 'Permanent / Contract' },
                  { role: 'Threat Modelling Specialist', type: 'Permanent / Contract' },
                ]?.map((role, i) => (
                  <div key={i} className="bg-[#0a0f14] px-6 py-5 flex items-center justify-between group hover:bg-[#111519] transition-colors cursor-pointer">
                    <div>
                      <p className="text-sm text-[#f5f5f8] font-sans">{role?.role}</p>
                      <p className="text-xs text-[#444] mt-1 font-mono-brand uppercase tracking-widest">{role?.type}</p>
                    </div>
                    <svg className="w-4 h-4 text-[#333] group-hover:text-[#80edfa] transition-colors" viewBox="0 0 16 16" fill="none">
                      <path d="M2 8H14M9 3L14 8L9 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="max-w-[700px] fade-in-up">
            <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-4">[Work with us]</p>
            <h2 className="font-heading text-[clamp(2rem,4vw,3.5rem)] text-[#f5f5f8] font-medium leading-tight mb-6">
              Ready to engineer security in?
            </h2>
            <p className="text-[#aaa] text-lg leading-relaxed mb-8">
              Whether you need rapid audit readiness, continuous DevSecOps, or a bespoke security programme, start with a discovery call. No sales pitch. A real conversation about your situation.
            </p>
            <Link href="/contact" className="btn-primary">Book a discovery call</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
